import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { ref as databaseRef, set } from 'firebase/database';
import { storage, dbRealtime } from '../environments/firebase';

// SVG Icons (simplified to only what's needed)
const ExpandIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>;
const CompressIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 7m0 0H9m5 0v5m-5 7 7-7m0 0h6m-6 0v-6"/></svg>;
const PencilIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>;
const TextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 6.1H3m13 6H3m7 6H3m13-6h4m-4 0-2 2m0 0-2-2m2 2v-4"/></svg>;
const ImageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const ColorPaletteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
const EraserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 21h10"/><path d="M12 21l9-9"/><path d="M3 12l9 9"/></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#000000'];

interface UploadedImage {
  url: string;
  name: string;
}

export default function Pravachan() {
  const [vakta, setVakta] = useState('');
  const [topic, setTopic] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [eraserMode, setEraserMode] = useState(false);
  const [drawingData, setDrawingData] = useState<Array<{path: string; color: string}>>([]);
  const [currentPath, setCurrentPath] = useState<{path: string; color: string} | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [uploadStatus, setUploadStatus] = useState<{[key: string]: 'uploading' | 'success' | 'error'}>({});
  const { currentUser } = useAuth();
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDrawingRef = useRef(false);

  // Set current date and time on component mount
  useEffect(() => {
    const now = new Date();
    setDateTime(now.toISOString().slice(0, 16));
  }, []);

  // Handle drawing
  useEffect(() => {
    if (!drawingMode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    
    const getCoordinates = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
      const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      isDrawingRef.current = true;
      const { x, y } = getCoordinates(e);
      setCurrentPath({ path: `M${x},${y}`, color: eraserMode ? '#f3f4f6' : selectedColor });
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      if (!isDrawingRef.current) return;
      const { x, y } = getCoordinates(e);
      setCurrentPath(prev => ({ path: `${prev?.path || ''} L${x},${y}`, color: eraserMode ? '#f3f4f6' : selectedColor }));
    };

    const endDrawing = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      if (currentPath && currentPath.path.length > 10) {
        setDrawingData(prev => [...prev, currentPath]);
      }
      setCurrentPath(null);
    };

    // Mouse events
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', endDrawing);
    canvas.addEventListener('mouseleave', endDrawing);
    
    // Touch events
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', endDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', endDrawing);
      canvas.removeEventListener('mouseleave', endDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', endDrawing);
    };
  }, [drawingMode, currentPath, selectedColor, eraserMode]);

  // Auto-resize textareas
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
    if (descriptionRef.current) {
      descriptionRef.current.style.height = 'auto';
      descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`;
    }
  }, [notes, description]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;
  
    try {
      setIsSubmitting(true);
      
      for (const file of Array.from(files)) {
        const fileName = `${Date.now()}_${file.name}`;
        setUploadStatus(prev => ({ ...prev, [fileName]: 'uploading' }));
  
        try {
          const fileRef = storageRef(storage, `Pravachan/${fileName}`);
          const snapshot = await uploadBytes(fileRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
  
          setUploadedImages(prev => [...prev, { url: downloadURL, name: fileName }]);
          setUploadStatus(prev => ({ ...prev, [fileName]: 'success' }));
        } catch (error) {
          console.error('Upload failed:', error);
          setUploadStatus(prev => ({ ...prev, [fileName]: 'error' }));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteImage = async (image: UploadedImage) => {
    try {
      const imageRef = storageRef(storage, `Pravachan/${image.name}`);
      await deleteObject(imageRef);
      setUploadedImages(prev => prev.filter(img => img.url !== image.url));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSubmitting(true);

    try {
      const timestamp = new Date(dateTime).getTime();
      const pravachanRef = databaseRef(dbRealtime, `users/${currentUser.uid}/MsmAshirwad/${timestamp}`);
      
      await set(pravachanRef, {
        vakta: vakta.trim(),
        topic: topic.trim(),
        dateTime: dateTime,
        description: description.trim(),
        notes: notes.trim(),
        drawing: drawingData,
        images: uploadedImages.map(img => img.url),
        createdAt: { '.sv': 'timestamp' }
      });

      // Reset form after successful submission
      setVakta('');
      setTopic('');
      setDescription('');
      setNotes('');
      setDrawingData([]);
      setUploadedImages([]);
      setDateTime(new Date().toISOString().slice(0, 16));
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className={`max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden ${isNotesExpanded ? 'fixed inset-0 m-0 z-50 rounded-none' : ''}`}>
        <form 
          onSubmit={handleSubmit} 
          className="flex flex-col h-full"
          style={{ maxHeight: isNotesExpanded ? '100vh' : 'calc(100vh - 2rem)' }}
        >
          {!isNotesExpanded && (
            <h2 className="text-2xl font-bold text-gray-800 p-6 pb-0 text-center">📝 Msm - Ashirwad Notes</h2>
          )}
          
          <div className="p-6 space-y-6 flex-1 overflow-auto">
            {!isNotesExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Location :</label>
                  <input
                    type="text"
                    value={vakta}
                    onChange={(e) => setVakta(e.target.value)}
                    placeholder="Enter Location name"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Topic:</label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Enter pravachan topic"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>
              </div>
            )}

            {!isNotesExpanded && (
              <div>
                <label className="block text-gray-700 font-medium mb-2">Date & Time:</label>
                <input
                  type="datetime-local"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>
            )}

            {!isNotesExpanded && (
              <div>
                <label className="block text-gray-700 font-medium mb-2">Describe Sabha:</label>
                <textarea
                  ref={descriptionRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the sabha atmosphere, Mahant Swami Maharaj's appearance, and any special moments"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1 italic">
                  Imagine: Mahant Swami Maharaj is addressing devotees, wearing his pagh, with Neelkanth Varni in the background.
                </p>
              </div>
            )}

            <div className="flex-1 flex flex-col">
              <div className="flex justify-between items-center mb-2 sticky top-0 bg-white py-2 z-10">
                <label className="block text-gray-700 font-medium"> Guruhari Ashirwad :</label>
                <div className="flex space-x-2">
                  {drawingMode && (
                    <>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          className={`p-2 rounded-lg ${showColorPicker ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                          title="Select Color"
                        >
                          <ColorPaletteIcon />
                        </button>
                        {showColorPicker && (
                          <div className="absolute right-0 mt-2 w-32 p-2 bg-white rounded-lg shadow-lg z-50 grid grid-cols-3 gap-2">
                            {COLORS.map(color => (
                              <button
                                key={color}
                                type="button"
                                className={`w-6 h-6 rounded-full ${color === '#ffffff' ? 'border border-gray-300' : ''}`}
                                style={{ backgroundColor: color }}
                                onClick={() => {
                                  setSelectedColor(color);
                                  setShowColorPicker(false);
                                  setEraserMode(false);
                                }}
                                title={color}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setEraserMode(!eraserMode)}
                        className={`p-2 rounded-lg ${eraserMode ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}
                        title={eraserMode ? 'Disable Eraser' : 'Enable Eraser'}
                      >
                        <EraserIcon />
                      </button>
                    </>
                  )}
                  
                  <button
                    type="button"
                    onClick={() => setDrawingMode(!drawingMode)}
                    className={`p-2 rounded-lg ${drawingMode ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                    title={drawingMode ? 'Switch to Text Mode' : 'Switch to Drawing Mode'}
                  >
                    {drawingMode ? <TextIcon /> : <PencilIcon />}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-lg bg-gray-100 text-gray-600"
                    title="Upload Image"
                  >
                    <ImageIcon />
                  </button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                    multiple
                  />
                  
                  {drawingMode && (
                    <button
                      type="button"
                      onClick={() => setDrawingData([])}
                      className="p-2 rounded-lg bg-red-100 text-red-600"
                      title="Clear Drawing"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsNotesExpanded(!isNotesExpanded)}
                    className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                    title={isNotesExpanded ? 'Minimize Notes' : 'Maximize Notes'}
                  >
                    {isNotesExpanded ? <CompressIcon /> : <ExpandIcon />}
                  </button>
                </div>
              </div>

              {drawingMode ? (
                <div 
                  ref={canvasRef}
                  className="flex-1 bg-gray-50 rounded-lg border border-gray-300 relative touch-none min-h-[300px]"
                  style={{ height: isNotesExpanded ? 'calc(100vh - 180px)' : '400px' }}
                >
                  <svg className="w-full h-full">
                    {drawingData.map((item, index) => (
                      <path 
                        key={index} 
                        d={item.path} 
                        stroke={item.color} 
                        strokeWidth={eraserMode ? '8' : '2'}
                        fill="none" 
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ))}
                    {currentPath && (
                      <path 
                        d={currentPath.path} 
                        stroke={currentPath.color} 
                        strokeWidth={eraserMode ? '8' : '2'}
                        fill="none" 
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  <textarea
                    ref={textareaRef}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="• Start typing your notes here..."
                    className="flex-1 w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none min-h-[200px]"
                    style={{ minHeight: isNotesExpanded ? 'calc(100vh - 220px)' : '200px' }}
                  />
                  {uploadedImages.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {uploadedImages.map((image) => (
                        <div key={image.url} className="relative group">
                          <div className="w-20 h-20 rounded border border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center">
                            <img 
                              src={image.url} 
                              alt="Uploaded"
                              className="w-full h-full object-contain"
                            />
                            <div className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs ${
                              uploadStatus[image.name] === 'success' ? 'bg-green-500' : 
                              uploadStatus[image.name] === 'error' ? 'bg-red-500' : 'bg-blue-500'
                            }`}>
                              {uploadStatus[image.name] === 'success' ? <CheckIcon /> : 
                              uploadStatus[image.name] === 'error' ? <CloseIcon /> : '...'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteImage(image)}
                            className="absolute top-0 right-0 -mt-2 -mr-2 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                            title="Delete image"
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 sticky bottom-0 bg-white">
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition shadow-md disabled:opacity-70"
              >
                {isSubmitting ? 'Saving...' : 'Save Ashirwad'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}